import {defineStore} from 'pinia'
import {useLocalStorage} from '@vueuse/core'
import type {ContractDetail, FullContract} from '~/models/contract'
import {
    CONTRACT_TYPE_FOREST_OPERATION,
    CONTRACT_TYPE_REGISTRATION, CONTRACT_TYPE_SUPPLY_WOOD,
    MS_DELAY_CRM_TO_VH_SYNC,
    MS_INTERVAL_CLEAN_MODIFIED_TIMESTAMPS
} from "~/constants/contracts";
import {DataType, RequestType} from "~/models/queue";
import {
    cleanDraftContract,
    contractIsExcludedFromForestFund,
    getHasOutdatedPeriod,
    isDraft,
    isForestFundValid
} from "~/helpers/contracts";
import {useQueueDb} from "~/use/indexedDb/queueDb";
import {useQueueSizeStore} from "~/use/ui/queueSize";
import {generateUUID} from "~/helpers/uuid";
import {cloneDeep} from "lodash";
import {
    STATUS_SBL_PLANNED,
    STATUS_SBL_SIGNED
} from "~/constants/contract";
import {isStatusSblBefore} from '~/helpers/statusSbl'
import {getContractCustomer} from "~/helpers/contractHelper";
import {useIndexedDBStorage} from "~/composables/useIndexedDBStorage";
import {GmModelLibraryModelsAssortmentModel} from "~/gen/openapi/sblService";
import {featureEnabled} from "~/helpers/features";
import {requiresSubType} from "~/helpers/assortmentsHelper";
import {useAssortmentService} from "~/services/assortmentService";
import {queryClient} from "~/plugins/vue-query";
import {useContractService} from "~/services/contractService";
import {useEntrepreneurService} from "~/services/entrepreneurService";
import {
    ProductionTypeFelling,
    ProductionTypeFellingAndDelivery,
    ProductionTypeRoadDelivery
} from "~/constants/productionOrders";
import {
    contractTypeShouldHaveEnvironmentReport,
    hasDuplicateProductionTypes,
    hasExpiredPeriods
} from "~/helpers/validations";
import {type ITaskListItem} from "@gm/components";

/**
 * @message Displayable error message
 * @path Link path the error message will lead the user to
 * @pathName Displayable name
 * @anchor Hash anchor - must include #
 */
export interface ContractActivationError {
    message: string;
    path?: string
    pathName?: string
    anchor?: string
}

export const useContractStore = defineStore('contract', () => {
    /**
     * Editable models for contracts.
     * For read-only usage of contract data or the reference contract, use data from contractService.
     *
     * Not to be confused with local drafts stored in draftsDb.
     */
    const contractDrafts = useIndexedDBStorage<Record<string, FullContract>>(
        'pinia/contracts/contractDrafts',
        {},
        {
            mergeDefaults: true,
            migrateFromLocalStorage: true,
            clearLocalStorageAfterMigration: true
        },
    )
    
    const { t } = useGlobalTranslation();

    /**
     * When a contract last edited properties that needs syncing.
     * Activation should wait after these changes so CRM and VH
     * have a chance to catch up syncing first.
     *
     * Similar to Production Orders locking.
     * Prevents user getting errors caused by activation while sync is ongoing.
     * Should be replaced in the future™.
     * */
    const modifiedPropsThatNeedSyncingTimestamps = useLocalStorage<{ [key: string]: number }>('pinia/contracts/contractModifiedPropsThatNeedSyncingTimestamps', {} as { [key: string]: number })

    /**
     * Get a reference to the contract draft.
     * Use this as a model to write to when editing a contract.
     *
     * @param id
     * @param defaultDraft - model to add if it doesn't already exist
     * @param overWriteDraft - force the draft in store to be overwritten with defaultDraft
     */
    const getContractDraftById = (id: string, defaultDraft?: FullContract, overWriteDraft = false) => {
        if (defaultDraft && (!contractDrafts.value[id] || overWriteDraft)) {
            contractDrafts.value[id] = cloneDeep(defaultDraft)
        }

        return computed({
            get: () => contractDrafts.value[id],
            set: (val: FullContract) => contractDrafts.value[id] = val
        })
    }

    const addContractDraft = (contractDraft: FullContract) => {
        if (contractDraft.Id && !contractDrafts.value[contractDraft.Id]) {
            contractDrafts.value[contractDraft.Id] = cloneDeep(contractDraft)
        }
    }


    const contractDraftExists = (id: string) => {
        return !!contractDrafts.value[id]
    }

    /**
     * NB! Not needed when using {@link getContractDraftById} to get a model.
     * This function should only be used to overwrite the whole contract draft, i.e. resetting changes or making large changes without a user input field
     * @param updatedContractDraft
     */
    const updateContractDraft = (updatedContractDraft: FullContract) => {
        if (updatedContractDraft.Id && contractDraftExists(updatedContractDraft.Id)) {
            contractDrafts.value[updatedContractDraft.Id] = cloneDeep(updatedContractDraft)
        }
    }

    const removeContractDraftById = (id: string) => {
        if (contractDrafts.value[id]) {
            delete contractDrafts.value[id]
        }
    }

    /**
     * Cleans up outdated modifiedTimestamps
     */
    function cleanModifiedTimestamps() {
        const aLittleWhileAgo = new Date().getTime() - MS_DELAY_CRM_TO_VH_SYNC

        modifiedPropsThatNeedSyncingTimestamps.value = {
            ...Object.fromEntries(
                Object.entries(modifiedPropsThatNeedSyncingTimestamps.value).filter(
                    // Only include relevant timestamps
                    ([_, lastModifiedTimestamp]) => lastModifiedTimestamp > aLittleWhileAgo
                )
            )
        }
    }

    function touchModifiedPropsThatNeedSyncing(contractId: string, time?: number) {
        modifiedPropsThatNeedSyncingTimestamps.value = {
            ...modifiedPropsThatNeedSyncingTimestamps.value,
            [contractId]: time ?? new Date().getTime()
        }
    }

    const getActivationLockedUntil = (contractId: string) => {
        const ts = modifiedPropsThatNeedSyncingTimestamps.value[contractId]
        if (ts) {
            return ts + MS_DELAY_CRM_TO_VH_SYNC
        }
        return 0
    }

    const getUrlAndMethod = (contract: FullContract): { url: string; method: RequestType } => {
        let url: string
        let method: RequestType
        if (isDraft(contract)) {
            url = '/v1/contracts'
            method = RequestType.post
        } else {
            url = `/v1/contracts/${contract.Id}`
            method = RequestType.put
        }

        return {
            url,
            method
        }
    }

    // make sure to clean up local state to avoid cluttering localStorage too much
    setInterval(cleanModifiedTimestamps, MS_INTERVAL_CLEAN_MODIFIED_TIMESTAMPS)

    const addContractToQueue = async (contract: FullContract): Promise<void> => {
        const { url, method } = getUrlAndMethod(contract)
        const { queueDb } = useQueueDb()
        const { updateQueueSize } = useQueueSizeStore()
        const cleanedContract = isDraft(contract)
            ? cleanDraftContract(contract)
            : contract
        const customer = getContractCustomer(contract)

        // Check if contract is already in queue
        // If in queue pass id as key to `setById`
        const queue = await queueDb.getAll()
        const existing = queue.find((item) => item.payload.TempId === contract.Id)

        await queueDb.setById({
            Id: existing?.Id || generateUUID(),
            url,
            date: new Date(),
            requestType: method,
            payload: cleanedContract,
            type: DataType.Contract,
            title: customer?.Name!,
        }).then(async () => {
            const contractService = useContractService()
            contractService.updateContractInContractList(contract)
            await queryClient.invalidateQueries({queryKey: ['contractGetContract', existing?.Id], refetchType: "none"})
            await queryClient.invalidateQueries({queryKey: ['contractGetContractList'], refetchType: "none"})
        })

        await updateQueueSize()
    }

    const activationValidationErrors = (contract?: Ref<FullContract>, contractReference?: Ref<FullContract>): ComputedRef<ITaskListItem[]> => {
        return computed(() => {
            const { getEntrepreneurById } = useEntrepreneurService()
            const runtimeConfig = useRuntimeConfig()
            const errors: Array<ITaskListItem> = []
            if (!contract?.value || !contractReference?.value) {
                return errors
            }
            const currentContractHasOutdatedPeriod = getHasOutdatedPeriod(contract.value)
            const { getAssortmentsQueryOptions } = useAssortmentService()
            const assortments = queryClient.getQueryData(getAssortmentsQueryOptions.queryKey) as Array<GmModelLibraryModelsAssortmentModel>

            const activeDetails = Array.isArray(contract.value.Details) ? contract.value.Details.filter(detail => !detail.Deleted) : []
            const isPlanningStage = isStatusSblBefore(contractReference.value.StatusSbl, STATUS_SBL_PLANNED);
            const isNotSigned = isStatusSblBefore(contractReference.value?.StatusSbl, STATUS_SBL_SIGNED);

            const isForestOperation = contract.value.Type === CONTRACT_TYPE_FOREST_OPERATION;
            const isSupplyWood = contract.value.Type === CONTRACT_TYPE_SUPPLY_WOOD;
            const isRegistration = contract.value.Type === CONTRACT_TYPE_REGISTRATION;
            
            if(isNotSigned) {
            errors.push(
                {
                    id: "notSignedByForestOwner",
                    label: t('AGREEMENT.TASKLIST.SIGNED_BY_FOREST_OWNER'),
                    required: true,
                    type: 'error',
                })
            }
            
            if (!contractReference.value.VsysNo && featureEnabled(runtimeConfig.public.FEATURE_REQUIRE_VSYS_ID_FOR_CONTRACT_ACTIVATION)) {
                errors.push({
                    id: "missing-vsys",
                    label: t('ACTIVATION_ERROR_MISSING_VSYS_NO'),
                    required: true,
                    type: 'error',
                })
            }
            if (currentContractHasOutdatedPeriod) {
                errors.push({
                    id: "contract-price-period",
                    label: t('AGREEMENT.TASKLIST.PRICE_PERIODS'),
                    required: true,
                    type: 'error',
                    path: '#contract-information',
                    pathName: t('EDIT')
                })
            }
            if (!activeDetails?.length) {
                errors.push({
                    id: "minimum-one-assortment",
                    label: t('AGREEMENT.TASKLIST.MINIMUM_ONE_ASSORTMENT'),
                    required: true,
                    type: 'error',
                    path: '#contract-details',
                    pathName: t('ADD')
                })
            }

            const hasMissingPriceList = activeDetails && activeDetails.some((detail) => !detail.TimberPriceListId)
            const hasMissingVolume = activeDetails && activeDetails.some((detail) => !detail.Quantity)
            const hasMissingPeriod = activeDetails && activeDetails.some((detail) => !detail.Period)
            
            if(activeDetails.length > 0 && (hasMissingPriceList || hasMissingVolume || hasMissingPeriod))
            {
                errors.push({
                    id: "invalid-assortments",
                    label: t('AGREEMENT.TASKLIST.VALID_ASSORTMENTS'),
                    required: true,
                    type: 'error',
                    path: '#contract-details',
                    pathName: t('EDIT')
                })
            }
            
            if (!contract.value.TopplePlaces || contract.value.TopplePlaces.filter(t => !t.Deleted).length === 0) {
                errors.push({
                    id: "minimum-one-topple-place",
                    label: t('AGREEMENT.TASKLIST.MINIMUM_ONE_TOPPLE_PLACE'),
                    required: true,
                    type: 'error',
                    path: '#topple-places',
                    pathName: t('ADD')
                })
            }

            
            const productionOrders = contract.value.ProductionOrders ?? [];
            const hasFelling = productionOrders.some(po => po.ProductionTypeCode === ProductionTypeFelling || po.ProductionTypeCode === ProductionTypeFellingAndDelivery);
            const hasRoadDelivery = productionOrders.some(po => po.ProductionTypeCode === ProductionTypeRoadDelivery || po.ProductionTypeCode === ProductionTypeFellingAndDelivery);
            let missingEntrepreneurLabel = "AGREEMENT.TASKLIST.MINIMUM_ONE_ENTREPRENEUR";
            if(!hasFelling && hasRoadDelivery) 
                missingEntrepreneurLabel = "AGREEMENT.TASKLIST.MINIMUM_ONE_ENTREPRENEUR_FELLING";
            else if(hasFelling && !hasRoadDelivery) 
                missingEntrepreneurLabel = "AGREEMENT.TASKLIST.MINIMUM_ONE_ENTREPRENEUR_ROAD_DELIVERY";
            
            if (!isPlanningStage && !contract.value.UseLargeForestOwnerFlow && (productionOrders.length === 0 || !hasFelling || !hasRoadDelivery)) {
                errors.push({
                    id: "missing-entrepreneur",
                    label: t(missingEntrepreneurLabel),
                    required: true,
                    type: 'error',
                    path: `#entrepreneurs`,
                    pathName: t('ADD')
                })
            }
            if (contract.value.UseLargeForestOwnerFlow) {
               
                if (!isPlanningStage && !contract.value.ProductionOrders?.every((order) => order.Entrepreneur?.Id)) {
                  errors.push({
                      id: "large-forest-owner-missing-entrepreneur",
                      label: t('AGREEMENT.TASKLIST.LARGE_FOREST_OWNER_MINIMUM_ONE_ENTREPRENEUR'),
                      required: true,
                      type: 'error',
                  })
                }
                if (!contract.value.EnvironmentReport?.SignedEnvironmentalReportForestOwner) {
                    errors.push({
                        id: "unsigned-environmental-report-forest-owner",
                        label: t('AGREEMENT.TASKLIST.LARGE_FOREST_OWNER_FLOW_ENVIRONMENT_REPORT_NOT_SIGNED'),
                        required: true,
                        type: 'error',
                    })
                }
            } else if (contractTypeShouldHaveEnvironmentReport(contract.value.Type!) && !contract.value.EnvironmentReport?.SignedForestryManager) {
                errors.push({
                    id: "unsigned-environmental-report",
                    label: t('AGREEMENT.TASKLIST.SIGNED_WORK_ORDER'),
                    required: true,
                    type: 'error',
                    path: `/customers/${contract.value.ForestOwnerId}/${contract.value.PropertyId}/${contract.value.Id}/environmentReport`,
                    pathName: t('FILL_OUT')
                })
            }

            if (activeDetails?.some((detail: ContractDetail) => {
                return requiresSubType(detail, toRef(assortments)) && !detail.WoodSpecies?.length
            })) {
                errors.push({
                    id: "missing-assortments-sub-type",
                    label: t('AGREEMENT.TASKLIST.MISSING_ASSORTMENTS_SUB_TYPE'),
                    required: true,
                    type: 'error',
                    path: '#contract-details',
                    pathName: t('EDIT')
                })
            }

            if (contract.value.AssortmentInformation?.EntrepreneurId) {
                const entrepreneur = computed(() => getEntrepreneurById(contract.value.AssortmentInformation?.EntrepreneurId!))
                if (contract.value.Type && [CONTRACT_TYPE_REGISTRATION, CONTRACT_TYPE_SUPPLY_WOOD, CONTRACT_TYPE_FOREST_OPERATION].includes(contract.value.Type) && !entrepreneur.value?.VsysNo) {
                    errors.push({
                        id: "missing-entrepreneur-vsys",
                        label: t('AGREEMENT.TASKLIST.MISSING_ENTREPRENEUR_VSYS'),
                        required: true,
                        type: 'error',
                    })
                }
            }

            if (hasDuplicateProductionTypes(contract.value.ProductionOrders)) {
                errors.push({
                    id: "duplicate-entrepreneur-type",
                    label: t('AGREEMENT.TASKLIST.DUPLICATE_PRODUCTION_TYPES_ERROR'),
                    required: true,
                    type: 'error',
                    path: '#entrepreneurs',
                    pathName: t('EDIT')
                })
            }

            if (activeDetails && hasExpiredPeriods(activeDetails)) {
                errors.push({
                    id: "expired-assortment-period",
                    label: t('AGREEMENT.TASKLIST.ASSORTMENTS_HAS_EXPIRED_PERIODS'),
                    required: true,
                    type: 'error',
                    path: '#contract-details',
                    pathName: t('EDIT')
                })
            }            

            const missingForestFund = !isForestFundValid(contractIsExcludedFromForestFund(contract.value), contract.value.ForestFundRate!);

            const generalErrors = {
                id: "agreementErrors",
                label: t('AGREEMENT.TASKLIST.AGREEMENT_ERRORS'),
                required: true,
                type: "error",
                pathName: t('RECTIFY')
            }
            
            if (missingForestFund) {
                errors.push({
                    ...generalErrors,
                    path: '#contract-information'
                })
            }
            
            const project = contract.value.Project;
            if(!isPlanningStage) {
                if(!project || (project && !project.ProjectManager?.Id || !project.ProjectAssistant?.Id)) {
                    errors.push({
                        id: "managerError",
                        label: t('AGREEMENT.TASKLIST.MISSING_CONTRACT_MANAGER'),
                        required: true,
                        type: "error",
                        path: "#contract-managers",
                        pathName: t('RECTIFY')
                        
                    })
                }
            }

            return errors
        })
    }

    return {
        contractDrafts,
        modifiedPropsThatNeedSyncingTimestamps,
        getActivationLockedUntil,
        activationValidationErrors,
        getContractDraftById,
        contractDraftExists,
        addContractDraft,
        updateContractDraft,
        removeContractDraftById,
        touchModifiedPropsThatNeedSyncing,
        addContractToQueue,
    }
})